import axios from "axios"
import { useAuthStore, useAppNotificationStore } from "../stores"
import { BASEURL } from "./constants"
import { useNavigate } from "react-router-dom"

type Request = {
  /**
   * the request endpoint
   *
   * OR
   *
   * the external resource url
   */
  url: string
  /**
   * the request body for POST, PUT, PATCH, and DELETE methods
   */
  body?: object | string | URLSearchParams | FormData | File | Blob
  params?: URLSearchParams
  headers?: Headers | {}
  external?: boolean
}
type RequestMethod = "GET" | "POST" | "PATCH" | "DELETE" | "PUT" | "HEAD"

const requestCore = async (
  method: RequestMethod,
  config: Request,
  axiosConfig?: any
) => {
  try {
    let { url, body, params, headers, external } = config

    external = external ?? false
    body = method === "GET" || method === "HEAD" ? undefined : body
    headers =
      headers === undefined
        ? { "Content-Type": "application/json" }
        : { "Content-Type": "application/json", ...headers }
    // , "Sec-Fetch-Mode": "no-cors"

    const response = await axios({
      baseURL: external === true ? url : BASEURL,
      url: external === true ? "" : url,
      method: method,
      headers: headers,
      data: body,
      params: params,
      ...axiosConfig,
      // responseType: "blob",
      validateStatus: function (status) {
        return status >= 200 && status < 500
      },
    })

    if (response.status === 401) {
      useAuthStore.getState().signOut()
      console.log(response?.statusText)
      return
    } else if (response.status === 403) {
      useAppNotificationStore.getState().toast.error(response?.data?.message)
      console.log(response?.statusText)
      return
    } else return response.data
  } catch (error: any) {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      if (error.response.status === 401) {
        // Handle 401 Unauthorized error
        console.log("Unauthorized request. Please check your credentials.")
      } else {
        // Handle other error codes
        console.log("Request failed with status:", error.response.status)
      }
    } else if (error.request) {
      // The request was made but no response was received
      console.log("No response received from the server.")
    } else {
      // Something happened in setting up the request that triggered an error
      console.log("Error:", error.message)
    }

    if (error.code === "ERR_FAILED" || error.code === "ERR_NETWORK") {
      useAppNotificationStore
        .getState()
        .toast.error("Please check your network connection!")
      return
    } else {
      console.log(error.response?.data.message ?? "")
      throw new Error(error?.response.statusText + ". Please try again later.")
    }
  }
}

/**
 * Make request easily to the API or other external resource by
 * passing true to the `external` option.
 */
export const request = {
  /**
   * Make /GET request
   * @param config request configurations
   * @returns Promise
   */
  get: async (config: Request, axiosConfig?: any) => {
    return await requestCore("GET", config, axiosConfig)
  },
  /**
   * Make /POST request
   * @param config request configurations
   * @returns Promise
   */
  post: async (config: Request) => {
    return await requestCore("POST", config)
  },
  /**
   * Make /POST request
   * @param config request configurations
   * @returns Promise
   */
  patch: async (config: Request) => {
    return await requestCore("PATCH", config)
  },
  /**
   * Make /PATCH request
   * @param config request configurations
   * @returns Promise
   */
  put: async (config: Request) => {
    return await requestCore("PUT", config)
  },
  /**
   * Make /DELETE request
   * @param config request configurations
   * @returns Promise
   */
  delete: async (config: Request) => {
    return await requestCore("DELETE", config)
  },
}
